import { Component } from 'react'
import { Offer, OfferBlock } from 'interfaces/offers'
import * as styles from 'components/features/pfm/OfferPage/OfferPage.css'
import Anchor from 'components/atoms/navigation/Anchor'
import * as cn from 'classnames'
import { CardBlock } from 'components/features/pfm/Blocks/Blocks'
import { RouteComponentProps } from 'react-router'
import CommonLayout from 'components/layouts/CommonLayout'
import TextButton from 'components/atoms/buttons/TextButton'
import { smoothScrollInfoView } from 'helpers/styleHelpers'
import LinkButton from 'components/atoms/buttons/LinkButton'
import fitty, { FittyInstance } from 'fitty'
import { OFFER_PAGE } from 'constants/pages'
import InternalLayoutContainer from 'components/features/pfm/InternalLayout/InternalLayoutContainer'
import InternalSidebarContainer from 'components/features/pfm/InternalSidebar/InternalSidebarContainer'
import { NotificationTrayWrapper } from 'components/features/pfm/NotificationTray/NotificationTrayWrapper'

type OfferPageState = {
  isDescriptionExpanded: boolean
  isTermsOpen: boolean
}

export type OfferPageStateProps = {
  offer: Offer
  isWide: boolean
}

export type OfferPageDispatchProps = {
  onMount: () => void
  onClickApply: () => void
}

export type OfferPageInnerProps = RouteComponentProps<{ id: string }>

export type OfferPageProps =
  & OfferPageStateProps
  & OfferPageDispatchProps

const APPLY_TEXT = 'Apply Now'
const DESCRIPTION_SHOWN_ITEMS = 3
const HIGHLIGHT_MIN_FONT_SIZE = parseInt(styles.highlightMinFontSize, 10)
const HIGHLIGHT_MAX_FONT_SIZE = parseInt(styles.highlightMaxFontSize, 10)

class OfferPage extends Component<OfferPageProps, OfferPageState> {

  state = {
    isDescriptionExpanded: false,
    isTermsOpen: false,
  }

  private termsNode: HTMLElement | null = null
  private fitties: FittyInstance[]

  render () {
    const { offer } = this.props
    const { id, headline, product, blocks } = offer

    const header = (
      <header className={ styles.header }>
        { headline }
      </header>
    )

    const pageBody = (
      <div className={ styles.container }>
        <CardBlock withPadding>
          <div className={ styles.product }>
            <div className={ styles.productTop }>
              <h2 className={ styles.productTitle }>
                { product.name }
              </h2>
              <h3 className={ styles.productSubtitle }>
                { product.header }
              </h3>
              <div className={ styles.imageColumn }>
                <img src={ product.image } className={ styles.image } />
                { this.renderApplyButton() }
              </div>
              { this.renderDescription() }
            </div>
            { this.renderHighlights() }
          </div>
        </CardBlock>
        <section className={ styles.blocks }>
          { blocks.map(this.renderBlock) }
        </section>
        <section className={ styles.footer }>
          { this.renderApplyButton() }
          { this.renderTerms() }
        </section>
      </div>
    )

    const notifications = (
      <NotificationTrayWrapper
        renderLevel='page'
        isPositionRelative
      />
    )

    return (
      <InternalLayoutContainer
        sidebar={ <InternalSidebarContainer /> }
        notifications={ notifications }
      >
        <Anchor placement={{ anchor: OFFER_PAGE, id }}>
          <CommonLayout header={ header }>
            { pageBody }
          </CommonLayout>
        </Anchor>
      </InternalLayoutContainer>
    )
  }

  renderDescription () {
    const { description } = this.props.offer.product
    const { isDescriptionExpanded } = this.state

    const showAll = isDescriptionExpanded || description.length <= DESCRIPTION_SHOWN_ITEMS
    const items = showAll
      ? description
      : description.slice(0, DESCRIPTION_SHOWN_ITEMS)
    const learnMore = showAll
      ? null
      : (
        <div className={ styles.expandDescription }>
          <TextButton size='regular' onClick={ this.handleExpandDescriptionClick }>Learn More</TextButton>
        </div>
      )

    return (
      <>
        <ul className={ styles.productDescription }>
          {
            items.map((item, idx) => (
              <li key={ idx } className={ styles.productDescriptionItem }>
                { item }
              </li>
            ))
          }
        </ul>
        { learnMore }
      </>
    )
  }

  renderHighlights () {
    const { highlights } = this.props.offer.product

    return (
      <div className={ styles.highlights }>
        {
          highlights.map((highlight, idx) => (
            <div key={ idx } className={ styles.highlight }>
              <div className={ styles.highlightInner }>
                <div className={ styles.hightlightLabel }>
                  { highlight.label }
                </div>
                <div className={ styles.highlightValue }>
                  { highlight.value }
                </div>
              </div>
            </div>
          ))
        }
      </div>
    )
  }

  renderBlock (block: OfferBlock, index: number) {
    const {
      color,
      layout,
      width,
    } = block.style

    const blockClassName = cn(styles.block, {
      [styles.halfWidth]: width === 'half',
    })

    const contentClassName = cn(
      styles.blockContent,
      styles[layout],
      styles[color],
    )

    const content = layout === 'divided'
      ? block.paragraphs.map((paragraph, idx) => {
        return (
          <div key={ idx } className={ contentClassName }>
            { paragraph.value }
          </div>
        )
      })
      : (
        <div className={ contentClassName }>
          {
            block.paragraphs.map((paragraph, idx) => {
              return (
                <div key={ idx } className={ styles.blockParagraph }>
                  { paragraph.label && (
                    <div className={ styles.blockParagraphLabel }>{ paragraph.label }</div>
                  )}
                  <div className={ styles.blockParagraphValue }>
                    { paragraph.value }
                  </div>
                </div>
              )
            })
          }
        </div>
      )

    return (
      <div key={ index } className={ blockClassName }>
        <h3 className={ styles.blockTitle }>{ block.title }</h3>
        { content }
      </div>
    )
  }

  renderApplyButton () {
    const { apply_link } = this.props.offer

    return (
      <div className={ styles.applyButtonWrapper }>
        <LinkButton
          href={ apply_link }
          target='_blank'
          size='responsive'
          text={ APPLY_TEXT }
          onClick={ this.handleApplyClick }
        />
      </div>
    )
  }

  renderTerms () {
    const { isTermsOpen } = this.state
    const { disclosure, terms } = this.props.offer

    return isTermsOpen
      ? (
        <div className={ styles.terms } ref={ (node) => { this.termsNode = node } }>
          { this.renderTermsSection(disclosure.label, disclosure.value) }
          { this.renderTermsSection(terms.label, terms.value) }
        </div>
      )
      : (
        <button className={ styles.expandTermsButton } onClick={ this.handleTermsButtonClick }>
          Advertiser Disclosure & Terms
        </button>
      )
  }

  renderTermsSection (label: string, value: string) {
    return (
      <div className={ styles.termsSection }>
        <div className={ styles.termsHeading }>
          { label }
        </div>
        <div className={ styles.termsText }>
          { value }
        </div>
      </div>
    )
  }

  componentDidMount () {
    this.props.onMount()

    this.fitties = fitty(`.${ styles.highlightValue }`, {
      minSize: HIGHLIGHT_MIN_FONT_SIZE,
      maxSize: HIGHLIGHT_MAX_FONT_SIZE,
    })
  }

  componentWillUnmount () {
    this.fitties.forEach((fittyInstance) => fittyInstance.unsubscribe())
  }

  private handleApplyClick = () => {
    this.props.onClickApply()
  }

  private handleExpandDescriptionClick = () => {
    this.setState({ isDescriptionExpanded: true })
  }

  private handleTermsButtonClick = () => {
    this.setState({ isTermsOpen: true }, () => {
      smoothScrollInfoView(this.termsNode, 'start')
    })
  }
}

export default OfferPage
